import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { User, Building2, Lock, Loader2, Eye, EyeOff, Check, X } from 'lucide-react';

const ProfilePage = () => {
  const { user, api, refreshUser, hasRole } = useAuth();
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [orgLoading, setOrgLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [organization, setOrganization] = useState(null);

  const [personalInfo, setPersonalInfo] = useState({
    first_name: '',
    last_name: '',
    phone: ''
  });

  const [companyInfo, setCompanyInfo] = useState({
    name: '',
    dba: '',
    primary_address: '',
    city: '',
    state: '',
    zip_code: '',
    primary_email: '',
    primary_phone: ''
  });

  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    if (user) {
      setPersonalInfo({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || ''
      });
    }
  }, [user]);

  const fetchOrganization = useCallback(async () => {
    try {
      const response = await api.get(`/organizations/${user.organization_id}`);
      setOrganization(response.data);
      setCompanyInfo({
        name: response.data.name || '',
        dba: response.data.dba || '',
        primary_address: response.data.primary_address || '',
        city: response.data.city || '',
        state: response.data.state || '',
        zip_code: response.data.zip_code || '',
        primary_email: response.data.primary_email || '',
        primary_phone: response.data.primary_phone || ''
      });
    } catch (error) {
      console.error('Failed to fetch organization:', error);
    }
  }, [api, user?.organization_id]);

  useEffect(() => {
    if (hasRole(['ADMIN']) && user?.organization_id) {
      fetchOrganization();
    }
  }, [user?.organization_id, hasRole, fetchOrganization]);

  const handlePersonalInfoSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put('/users/me', personalInfo);
      toast.success('Personal information updated successfully!');
      refreshUser();
    } catch (error) {
      toast.error('Failed to update personal information');
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyInfoSubmit = async (e) => {
    e.preventDefault();
    setOrgLoading(true);
    try {
      await api.put(`/organizations/${user.organization_id}`, companyInfo);
      toast.success('Company information updated successfully!');
      fetchOrganization();
    } catch (error) {
      toast.error('Failed to update company information');
    } finally {
      setOrgLoading(false);
    }
  };

  const passwordChecks = {
    length: passwordData.new_password.length >= 8,
    lowercase: /[a-z]/.test(passwordData.new_password),
    uppercase: /[A-Z]/.test(passwordData.new_password),
    number: /[0-9]/.test(passwordData.new_password)
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }

    if (!passwordChecks.length || !passwordChecks.lowercase || !passwordChecks.uppercase || !passwordChecks.number) {
      toast.error('Password does not meet requirements');
      return;
    }

    setPasswordLoading(true);
    try {
      await api.put('/users/me/password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
        confirm_password: passwordData.confirm_password
      });
      toast.success('Password changed successfully!');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to change password';
      toast.error(message);
    } finally {
      setPasswordLoading(false);
    }
  };

  const CheckItem = ({ checked, label }) => (
    <div className="flex items-center gap-2 text-sm">
      {checked ? (
        <Check className="w-4 h-4 text-green-500" />
      ) : (
        <X className="w-4 h-4 text-red-500" />
      )}
      <span className={checked ? 'text-green-700' : 'text-[#5A5A5A]'}>{label}</span>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fade-in" data-testid="profile-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#323033]">Profile Settings</h1>
        <p className="text-[#5A5A5A] mt-1">
          Manage your personal information and account settings
        </p>
      </div>

      <Tabs defaultValue="personal" className="space-y-6">
        <TabsList>
          <TabsTrigger value="personal" data-testid="personal-tab">
            <User className="w-4 h-4 mr-2" />
            Personal Info
          </TabsTrigger>
          {hasRole(['ADMIN']) && (
            <TabsTrigger value="company" data-testid="company-tab">
              <Building2 className="w-4 h-4 mr-2" />
              Company Info
            </TabsTrigger>
          )}
          <TabsTrigger value="password" data-testid="password-tab">
            <Lock className="w-4 h-4 mr-2" />
            Password
          </TabsTrigger>
        </TabsList>

        {/* Personal Information */}
        <TabsContent value="personal">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#323033]">
                Personal Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePersonalInfoSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">First Name</Label>
                    <Input
                      id="first_name"
                      value={personalInfo.first_name}
                      onChange={(e) => setPersonalInfo({ ...personalInfo, first_name: e.target.value })}
                      data-testid="profile-first-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name">Last Name</Label>
                    <Input
                      id="last_name"
                      value={personalInfo.last_name}
                      onChange={(e) => setPersonalInfo({ ...personalInfo, last_name: e.target.value })}
                      data-testid="profile-last-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      value={user?.email || ''}
                      disabled
                      className="bg-muted"
                    />
                    <p className="text-xs text-[#5A5A5A]">Email cannot be changed</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      value={personalInfo.phone}
                      onChange={(e) => setPersonalInfo({ ...personalInfo, phone: e.target.value })}
                      data-testid="profile-phone-input"
                    />
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    className="bg-[#005EF8] hover:bg-[#0047C2] text-white"
                    disabled={loading}
                    data-testid="save-personal-info-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Personal Info'
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Company Information (Admin only) */}
        {hasRole(['ADMIN']) && (
          <TabsContent value="company">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-[#323033]">
                  Company Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCompanyInfoSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="company_name">Company Name</Label>
                      <Input
                        id="company_name"
                        value={companyInfo.name}
                        onChange={(e) => setCompanyInfo({ ...companyInfo, name: e.target.value })}
                        data-testid="company-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="dba">DBA (Doing Business As)</Label>
                      <Input
                        id="dba"
                        value={companyInfo.dba}
                        onChange={(e) => setCompanyInfo({ ...companyInfo, dba: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="primary_address">Primary Address</Label>
                      <Input
                        id="primary_address"
                        value={companyInfo.primary_address}
                        onChange={(e) => setCompanyInfo({ ...companyInfo, primary_address: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company_city">City</Label>
                      <Input
                        id="company_city"
                        value={companyInfo.city}
                        onChange={(e) => setCompanyInfo({ ...companyInfo, city: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company_state">State</Label>
                      <Input
                        id="company_state"
                        value={companyInfo.state}
                        onChange={(e) => setCompanyInfo({ ...companyInfo, state: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company_zip">ZIP Code</Label>
                      <Input
                        id="company_zip"
                        value={companyInfo.zip_code}
                        onChange={(e) => setCompanyInfo({ ...companyInfo, zip_code: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="primary_email">Primary Email</Label>
                      <Input
                        id="primary_email"
                        type="email"
                        value={companyInfo.primary_email}
                        onChange={(e) => setCompanyInfo({ ...companyInfo, primary_email: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="primary_phone">Primary Phone</Label>
                      <Input
                        id="primary_phone"
                        value={companyInfo.primary_phone}
                        onChange={(e) => setCompanyInfo({ ...companyInfo, primary_phone: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button
                      type="submit"
                      className="bg-[#005EF8] hover:bg-[#0047C2] text-white"
                      disabled={orgLoading}
                      data-testid="save-company-info-btn"
                    >
                      {orgLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        'Save Company Info'
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Change Password */}
        <TabsContent value="password">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#323033]">
                Change Password
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordSubmit} className="space-y-6">
                <div className="space-y-4 max-w-md">
                  <div className="space-y-2">
                    <Label htmlFor="current_password">Current Password</Label>
                    <Input
                      id="current_password"
                      type="password"
                      value={passwordData.current_password}
                      onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                      required
                      data-testid="current-password-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="new_password">New Password</Label>
                    <div className="relative">
                      <Input
                        id="new_password"
                        type={showPassword ? 'text' : 'password'}
                        value={passwordData.new_password}
                        onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                        required
                        data-testid="new-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#808080] hover:text-[#323033]"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {passwordData.new_password && (
                      <div className="mt-2 grid grid-cols-2 gap-1">
                        <CheckItem checked={passwordChecks.length} label="8+ characters" />
                        <CheckItem checked={passwordChecks.lowercase} label="Lowercase" />
                        <CheckItem checked={passwordChecks.uppercase} label="Uppercase" />
                        <CheckItem checked={passwordChecks.number} label="Number" />
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirm_password">Confirm New Password</Label>
                    <Input
                      id="confirm_password"
                      type="password"
                      value={passwordData.confirm_password}
                      onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                      required
                      data-testid="confirm-password-input"
                    />
                    {passwordData.confirm_password && passwordData.new_password !== passwordData.confirm_password && (
                      <p className="text-sm text-red-500">Passwords do not match</p>
                    )}
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button
                    type="submit"
                    className="bg-[#005EF8] hover:bg-[#0047C2] text-white"
                    disabled={passwordLoading}
                    data-testid="update-password-btn"
                  >
                    {passwordLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Updating...
                      </>
                    ) : (
                      'Update Password'
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ProfilePage;
